import type { MatrixData } from '../components/MatrixTable'

import { normalizeToIsoZMidnight } from './dateNormalization'
import { findAvailableLotMatch, resolveCanonicalLotKey } from './lotIdentity'
import { isHatcheryStage, mortalityReference, type CriticalDay, type HatcheryDefaults, type LotLifeStage } from './hatchery'
import { interpolateMortalityRate } from './mortalityMatrix'
import { selectLatestDatedRow } from './waterSelection'

export type ProjectionWaterRow = {
  date: string
  lot: string
  /** Findings #138: the reading's lot id when it has one (canonical identity). */
  lotId?: string
  temperature: number | null
}

export type ProjectionAvailableLot = {
  id: string
  lotKey?: string | null
}

export type MortalityProjectionInputRow = {
  date: string
  lot?: string | null
  lotId?: string | null
  weight?: string | number | null
  fishStock?: string | number | null
}

export type MortalityProjectionResult = {
  temperatureC: number
  expectedMortalityRate: number
  projectedSurvivalRate: number
  projectedStock: number
  projectedBiomassKg: number
}

export type ObservedMortalityMetrics = {
  observedMortalityCount: number
  observedMortalityRate: number
  observedSurvivalRate: number
  previousKnownStock: number
  derivedEndOfDayStock: number
}

const MS_PER_DAY = 86_400_000

const parseNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number.parseFloat(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

const parseRequiredPositiveNumber = (value: unknown): number | null => {
  const parsed = parseNullableNumber(value)
  if (parsed === null || parsed <= 0) return null
  return parsed
}

const lotTokenFromLotId = (lotId: string): string => {
  return `lot-id:${lotId}`
}

const lotTokenFromLotKey = (lotKey: string): string => {
  const token = resolveCanonicalLotKey(lotKey)
  if (token === 'unknown-lot') return ''
  return `lot-key:${token}`
}

const resolveProjectionLotToken = (
  row: Pick<MortalityProjectionInputRow, 'lot' | 'lotId'>,
  availableLots: ReadonlyArray<ProjectionAvailableLot>
): string => {
  const explicitLotId = String(row.lotId ?? '').trim()
  if (explicitLotId) {
    return lotTokenFromLotId(explicitLotId)
  }

  const lotLabel = String(row.lot ?? '').trim()
  if (lotLabel) {
    const matchedLot = findAvailableLotMatch(lotLabel, availableLots)
    if (matchedLot.status === 'matched') {
      return lotTokenFromLotId(String(matchedLot.match.id ?? '').trim())
    }

    if (matchedLot.status === 'ambiguous') {
      // Avoid unsafe lot-label guessing when multiple lot IDs share one display label.
      return ''
    }

    const token = lotTokenFromLotKey(lotLabel)
    if (token) return token
  }

  return ''
}

export const getProjectionWeekKey = (date: string): string | null => {
  const normalized = normalizeToIsoZMidnight(date)
  if (!normalized.ok) return null

  const [yearStr, monthStr, dayStr] = normalized.isoDate.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null

  const MS_PER_DAY = 86_400_000
  const dateUtc = new Date(Date.UTC(year, month - 1, day))
  const yearStartUtc = new Date(Date.UTC(year, 0, 1))
  const diffDays = ((dateUtc.getTime() - yearStartUtc.getTime()) / MS_PER_DAY) + yearStartUtc.getUTCDay() + 1
  const weekNum = Math.ceil(diffDays / 7)
  if (!Number.isFinite(weekNum)) return null

  return `${year}-W${weekNum.toString().padStart(2, '0')}`
}

export const normalizeProjectionWaterLogs = (
  logs: Array<{ recordedAt?: string; lot?: string; lotId?: string | null; unmappedLot?: string; temperatureC?: string | number | null }>
): ProjectionWaterRow[] => (
  logs
    .map((log) => {
      const explicitLot = String(log.lot ?? '').trim()
      const unmappedLot = String(log.unmappedLot ?? '').trim()
      const lotId = String(log.lotId ?? '').trim()

      return {
        date: log.recordedAt ?? '',
        lot: explicitLot || unmappedLot,
        ...(lotId ? { lotId } : {}),
        temperature: parseNullableNumber(log.temperatureC)
      }
    })
    .filter((row) => Boolean((row.lot || row.lotId) && row.date))
)

export const deriveObservedMortalityMetrics = (args: {
  previousFishStock: unknown
  dailyMortality: unknown
}): ObservedMortalityMetrics | null => {
  const previousFishStock = parseRequiredPositiveNumber(args.previousFishStock)
  const dailyMortality = parseNullableNumber(args.dailyMortality)

  if (previousFishStock === null) return null
  if (dailyMortality === null || dailyMortality < 0) return null

  const derivedEndOfDayStock = Math.max(0, previousFishStock - dailyMortality)
  const observedMortalityRate = (dailyMortality / previousFishStock) * 100
  const observedSurvivalRate = (derivedEndOfDayStock / previousFishStock) * 100

  return {
    observedMortalityCount: dailyMortality,
    observedMortalityRate,
    observedSurvivalRate,
    previousKnownStock: previousFishStock,
    derivedEndOfDayStock
  }
}

const getUtcMidnightTime = (date: string): number | null => {
  const normalized = normalizeToIsoZMidnight(date)
  if (!normalized.ok) return null

  const [yearStr, monthStr, dayStr] = normalized.isoDate.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null

  return Date.UTC(year, month - 1, day)
}

const addUtcDays = (date: string, dayOffset: number): string | null => {
  const time = getUtcMidnightTime(date)
  if (time === null) return null
  return new Date(time + (dayOffset * MS_PER_DAY)).toISOString()
}

/** Findings #138: how far back the latest temperature reading may be. */
export const PROJECTION_TEMPERATURE_LOOKBACK_DAYS = 7

export type RecentTemperatureIndex = ReadonlyMap<string, ReadonlyArray<{ time: number; temperature: number }>>

/** Temperature readings per lot token, oldest first (findings #138). */
export const buildRecentTemperatureIndex = (
  waterRows: ReadonlyArray<ProjectionWaterRow>,
  availableLots: ReadonlyArray<ProjectionAvailableLot>
): RecentTemperatureIndex => {
  const index = new Map<string, Array<{ time: number; temperature: number }>>()
  for (const waterRow of waterRows) {
    if (waterRow.temperature === null || !waterRow.date) continue
    const time = Date.parse(waterRow.date)
    if (!Number.isFinite(time)) continue
    for (const lotToken of waterRowLotTokens(waterRow, availableLots)) {
      const list = index.get(lotToken) ?? []
      list.push({ time, temperature: waterRow.temperature })
      index.set(lotToken, list)
    }
  }
  index.forEach((list) => list.sort((a, b) => a.time - b.time))
  return index
}

/** The lot's latest temperature recorded by the end of `date`, at most
 *  `lookbackDays` back - not only the same calendar week (findings #138). */
export const findRecentTemperature = (
  index: RecentTemperatureIndex,
  lotToken: string,
  date: string,
  lookbackDays: number = PROJECTION_TEMPERATURE_LOOKBACK_DAYS
): number | null => {
  const dayStart = getUtcMidnightTime(date)
  if (dayStart === null) return null
  const end = dayStart + MS_PER_DAY
  const start = end - (lookbackDays * MS_PER_DAY)
  const readings = index.get(lotToken) ?? []
  for (let i = readings.length - 1; i >= 0; i -= 1) {
    const reading = readings[i]
    if (reading.time >= end) continue
    if (reading.time < start) return null
    return reading.temperature
  }
  return null
}

const resolveProjectionStep = (args: {
  lotToken: string
  date: string
  weight: number
  latestTemperatureByLotWeek: ReadonlyMap<string, ProjectionWaterRow>
  recentTemperatureIndex?: RecentTemperatureIndex
  speciesKey: string
  mortalityMatrix: MatrixData
}): { temperatureC: number; expectedMortalityRate: number } | null => {
  let matchedTemperature: number | null | undefined
  if (args.recentTemperatureIndex) {
    matchedTemperature = findRecentTemperature(args.recentTemperatureIndex, args.lotToken, args.date)
  } else {
    const week = getProjectionWeekKey(args.date)
    if (!week) return null
    matchedTemperature = args.latestTemperatureByLotWeek.get(`${args.lotToken}-${week}`)?.temperature
  }
  if (matchedTemperature === null || matchedTemperature === undefined) return null

  return {
    temperatureC: matchedTemperature,
    expectedMortalityRate: interpolateMortalityRate(args.weight, matchedTemperature, {
      species: args.speciesKey,
      matrix: args.mortalityMatrix
    })
  }
}

// A water reading's lot tokens: its lot id when it has one (findings #138 -
// lotId is the identity), else the label matched to the lots in scope.
const waterRowLotTokens = (
  waterRow: ProjectionWaterRow,
  availableLots: ReadonlyArray<ProjectionAvailableLot>
): string[] => {
  const explicitLotId = String(waterRow.lotId ?? '').trim()
  if (explicitLotId) return [lotTokenFromLotId(explicitLotId)]

  const lotLabel = String(waterRow.lot ?? '').trim()
  const matchedLot = lotLabel ? findAvailableLotMatch(lotLabel, availableLots) : { status: 'unmatched' as const }
  if (matchedLot.status === 'matched') {
    const matchedId = String(matchedLot.match.id ?? '').trim()
    return matchedId ? [lotTokenFromLotId(matchedId)] : []
  }
  if (matchedLot.status === 'ambiguous') {
    return matchedLot.matches
      .map((match) => String(match.id ?? '').trim())
      .filter(Boolean)
      .map(lotTokenFromLotId)
  }
  const fallbackToken = lotTokenFromLotKey(lotLabel)
  return fallbackToken ? [fallbackToken] : []
}

export const buildLatestTemperatureByLotWeek = (
  waterRows: ReadonlyArray<ProjectionWaterRow>,
  availableLots: ReadonlyArray<ProjectionAvailableLot>
): Map<string, ProjectionWaterRow> => {
  const latestByKey = new Map<string, ProjectionWaterRow>()

  for (const waterRow of waterRows) {
    if (!waterRow.date || waterRow.temperature === null) continue

    const week = getProjectionWeekKey(waterRow.date)
    if (!week) continue

    const lotTokens = waterRowLotTokens(waterRow, availableLots)

    lotTokens.forEach((lotToken) => {
      const selectionKey = `${lotToken}-${week}`
      const current = latestByKey.get(selectionKey)
      if (!current) {
        latestByKey.set(selectionKey, waterRow)
        return
      }

      const latest = selectLatestDatedRow([current, waterRow])
      if (latest) {
        latestByKey.set(selectionKey, latest)
      }
    })
  }

  return latestByKey
}

export const resolveMortalityProjectionForRow = (args: {
  row: MortalityProjectionInputRow
  availableLots: ReadonlyArray<ProjectionAvailableLot>
  latestTemperatureByLotWeek: ReadonlyMap<string, ProjectionWaterRow>
  /** Findings #138: when given, the temperature is the lot's latest reading
   *  of the past 7 days instead of one from the same calendar week. */
  recentTemperatureIndex?: RecentTemperatureIndex
  speciesKey: string
  mortalityMatrix: MatrixData
  daysToProject?: number
}): MortalityProjectionResult | null => {
  const { row, availableLots, latestTemperatureByLotWeek, recentTemperatureIndex, speciesKey, mortalityMatrix } = args

  const weight = parseRequiredPositiveNumber(row.weight)
  const fishStock = parseRequiredPositiveNumber(row.fishStock)
  if (weight === null || fishStock === null) return null

  const lotToken = resolveProjectionLotToken(row, availableLots)
  if (!lotToken) return null

  const daysToProject = Number.isFinite(args.daysToProject) && (args.daysToProject ?? 0) > 0
    ? Math.floor(args.daysToProject as number)
    : 1

  let projectedStock = fishStock
  let firstStep: { temperatureC: number; expectedMortalityRate: number } | null = null

  for (let dayOffset = 0; dayOffset < daysToProject; dayOffset += 1) {
    const projectionDate = addUtcDays(row.date, dayOffset)
    if (!projectionDate) return null

    const step = resolveProjectionStep({
      lotToken,
      date: projectionDate,
      weight,
      latestTemperatureByLotWeek,
      recentTemperatureIndex,
      speciesKey,
      mortalityMatrix
    })
    if (!step) return null

    firstStep ??= step

    const projectedMortalityCount = Math.min(
      projectedStock,
      Math.max(0, Math.round((projectedStock * step.expectedMortalityRate) / 100))
    )
    projectedStock = Math.max(0, projectedStock - projectedMortalityCount)
  }

  if (!firstStep) return null

  const projectedSurvivalRate = (projectedStock / fishStock) * 100
  const projectedBiomassKg = (projectedStock * weight) / 1000

  return {
    temperatureC: firstStep.temperatureC,
    expectedMortalityRate: firstStep.expectedMortalityRate,
    projectedSurvivalRate,
    projectedStock,
    projectedBiomassKg
  }
}

export type MortalitySeverity = 'green' | 'yellow' | 'red'

/**
 * Compares a row's observed daily mortality rate against the matrix-
 * projected expected rate for the same weight/temperature, for the
 * Datatable %Mort column's traffic-light indicator. Green: on or under
 * plan. Yellow: over plan but under 1.5x. Red: 1.5x plan or worse.
 * `expectedRatePct` of `0` is a legitimate "no mortality expected at all"
 * baseline — any observed mortality against it is red, not a division
 * edge case to special-case away.
 */
export const computeMortalitySeverity = (
  observedRatePct: number,
  expectedRatePct: number
): MortalitySeverity => {
  if (observedRatePct <= expectedRatePct) return 'green'
  if (observedRatePct >= expectedRatePct * 1.5) return 'red'
  return 'yellow'
}
/**
 * Findings #138: the weight to compare a day's mortality with - the row's
 * own weight, else the lot's last weight on an earlier row of the same
 * table (a mortality-only day keeps the last sampled weight).
 */
export const buildLastKnownWeightByRowId = (
  rows: ReadonlyArray<{ id?: unknown; date?: unknown; lot?: unknown; lotId?: unknown; weight?: unknown }>
): Map<string, number> => {
  const byLot = new Map<string, Array<{ id: string; date: string; weight: number | null }>>()
  for (const row of rows) {
    const id = String(row.id ?? '')
    const date = String(row.date ?? '').slice(0, 10)
    const lotKey = String(row.lotId ?? '').trim() || resolveCanonicalLotKey(String(row.lot ?? ''))
    if (!id || !date || !lotKey || lotKey === 'unknown-lot') continue
    const list = byLot.get(lotKey) ?? []
    list.push({ id, date, weight: parseRequiredPositiveNumber(row.weight) })
    byLot.set(lotKey, list)
  }
  const result = new Map<string, number>()
  byLot.forEach((list) => {
    list.sort((a, b) => a.date.localeCompare(b.date))
    let last: number | null = null
    let index = 0
    while (index < list.length) {
      // Rows of the same day share that day's weight.
      const day = list[index].date
      let end = index
      while (end < list.length && list[end].date === day) {
        if (list[end].weight !== null) last = list[end].weight
        end += 1
      }
      for (let i = index; i < end; i += 1) {
        if (last !== null) result.set(list[i].id, last)
      }
      index = end
    }
  })
  return result
}

export type MortalityDot =
  | { kind: 'severity'; severity: MortalitySeverity; expectedRatePct: number }
  | {
    kind: 'stageReference'
    severity: MortalitySeverity
    reference: 'eggs' | 'yolkSac' | 'critical'
    criticalDay: CriticalDay | null
    greenMaxPct: number
    redFromPct: number
  }
  | { kind: 'missing'; reason: 'noWeight' | 'noTemperature' }

/**
 * Findings #194: an early-stage day judged against the facility's daily
 * reference — green up to `greenMaxPct`, red from `redFromPct`, yellow between.
 */
export const stageReferenceSeverity = (observedRatePct: number, reference: { greenMaxPct: number; redFromPct: number }): MortalitySeverity => {
  if (observedRatePct >= reference.redFromPct) return 'red'
  if (observedRatePct <= reference.greenMaxPct) return 'green'
  return 'yellow'
}

/**
 * Findings #138: the Base de datos "Mortalidad diaria %" dot. Always one:
 * green / yellow / red against the matrix, or grey saying what is missing.
 * Findings #194: egg and yolk-sac days (the matrix works by fish weight) and
 * the critical days of early stages use the facility's daily reference.
 */
export const resolveMortalityDot = (args: {
  observedRatePct: number
  /** The lot's stage and critical day on the row's day; omitted for fish lots. */
  earlyStage?: { stage: LotLifeStage | null; criticalDay: CriticalDay | null; defaults: HatcheryDefaults }
  row: MortalityProjectionInputRow
  availableLots: ReadonlyArray<ProjectionAvailableLot>
  latestTemperatureByLotWeek: ReadonlyMap<string, ProjectionWaterRow>
  recentTemperatureIndex: RecentTemperatureIndex
  speciesKey: string
  mortalityMatrix: MatrixData
}): MortalityDot => {
  const early = args.earlyStage
  if (early && (early.criticalDay || isHatcheryStage(early.stage))) {
    const reference = early.criticalDay ? 'critical' : early.stage === 'eggs' ? 'eggs' : 'yolkSac'
    const limits = mortalityReference(early.defaults, reference)
    return {
      kind: 'stageReference',
      severity: stageReferenceSeverity(args.observedRatePct, limits),
      reference,
      criticalDay: early.criticalDay,
      ...limits
    }
  }
  if (parseRequiredPositiveNumber(args.row.weight) === null) return { kind: 'missing', reason: 'noWeight' }
  const projection = resolveMortalityProjectionForRow({
    row: args.row,
    availableLots: args.availableLots,
    latestTemperatureByLotWeek: args.latestTemperatureByLotWeek,
    recentTemperatureIndex: args.recentTemperatureIndex,
    speciesKey: args.speciesKey,
    mortalityMatrix: args.mortalityMatrix
  })
  if (!projection) return { kind: 'missing', reason: 'noTemperature' }
  return {
    kind: 'severity',
    severity: computeMortalitySeverity(args.observedRatePct, projection.expectedMortalityRate),
    expectedRatePct: projection.expectedMortalityRate
  }
}
